import {Color} from 'app/features/analytics/shared/components/analytics-table/analytics-table.component';
import {ColorSettings} from 'app/features/analytics/shared/components/analytics-table/components/form-coloring-cells/form-coloring-cells.component';
import {SelectedMeasure} from 'app/features/analytics/service/filter-value.interface';

export enum ReportsMode {
    SummaryReport,
    Monetization,
    UserAcquisition,
    TargetCPA,
    Demo,
    DemoEdit,
}

export function compareFunction(number1, number2): any {
    if (number1 === null && number2 === null) {
        return 0;
    }

    if (isNaN(number1) && isNaN(number2)) {
        if (number1 < number2) {
            return -1;
        }

        if (number1 > number2) {
            return 1;
        }

        return 0;
    }

    if (number1 === null) {
        return -1;
    }

    if (number2 === null) {
        return 1;
    }

    if (number1 > number2) {
        return 1;
    }
    return -1;
}

export const dateCompareFunction = (a, b) => compareFunction(a?.value, b?.value);

export const valueCompareFunction = (number1, number2): any => compareFunction(number1?.value, number2?.value);

// eslint-disable-next-line max-len
const semVerRegEx =
    // eslint-disable-next-line max-len
    /(?<Major>0|(?:[1-9]\d*))(?:\.(?<Minor>0|(?:[1-9]\d*))(?:\.(?<Patch>0|(?:[1-9]\d*)))?(?:\-(?<PreRelease>[0-9A-Z\.-]+))?(?:\+(?<Meta>[0-9A-Z\.-]+))?)?/;
export const semiVerCompareFunction = (a, b): number => semiVerCompare(a?.value || '', b?.value || '');

export const semiVerCompare = (a: string, b: string): number => {
    const amatch = semVerRegEx.exec(a.trim());
    const bmatch = semVerRegEx.exec(b.trim());
    if (amatch && !bmatch) {
        return amatch[1] > b ? -1 : 1;
    }
    if (!amatch && bmatch) {
        return bmatch[1] < a ? -1 : 1;
    }
    if (amatch && bmatch) {
        for (let i = 1; i <= 3; i++) {
            if (amatch[i] !== bmatch[i]) {
                return Number(bmatch[i]) - Number(amatch[i]);
            }
        }
        for (let i = 4; i <= 5; i++) {
            if (amatch[i] !== bmatch[i]) {
                return amatch[i] > bmatch[i] ? -1 : 1;
            }
        }
    }

    if (a === b) {
        return 0;
    }
    return a > b ? -1 : 1;
};

export function numberComparator(number1, number2, _, __, isAsc: boolean) {
    number1 = number1?.value;
    number2 = number2?.value;
    if (number1 === null && number2 === null) {
        return 0;
    }
    const inf = isAsc ? -Infinity : Infinity;
    number1 = number1 === '-' ? inf : Number(number1);
    number2 = number2 === '-' ? inf : Number(number2);
    return compareFunction(number1, number2);
}

export function getGradientNumbers(value: number, point1: number, point2: number, color1: Color, color2: Color): Color {
    let percent = (value - point1) / (point2 - point1);
    let diffRed = color2[0] - color1[0];
    let diffGreen = color2[1] - color1[1];
    let diffBlue = color2[2] - color1[2];

    return [
        Math.floor(color1[0] + percent * diffRed),
        Math.floor(color1[1] + percent * diffGreen),
        Math.floor(color1[2] + percent * diffBlue),
    ];
}

export function bgColorCell(value: number, colorSettings: ColorSettings) {
    if (!colorSettings || !colorSettings.colorsList) {
        return;
    }

    switch (true) {
        case value <= colorSettings.minValue:
            return `rgb(${colorSettings.colorsList[0]})`;
        case value >= colorSettings.maxValue:
            return `rgb(${colorSettings.colorsList[colorSettings.colorsList.length - 1]})`;
        case colorSettings.midValue === null:
            return colorToRgb(
                getGradientNumbers(
                    value,
                    colorSettings.minValue,
                    colorSettings.maxValue,
                    colorSettings.colorsList[0],
                    colorSettings.colorsList[colorSettings.colorsList.length - 1]
                )
            );
        case value > colorSettings.minValue && value < colorSettings.midValue:
            return colorToRgb(
                getGradientNumbers(
                    value,
                    colorSettings.minValue,
                    colorSettings.midValue,
                    colorSettings.colorsList[0],
                    colorSettings.colorsList[Math.floor(colorSettings.colorsList.length / 2)]
                )
            );
        case value > colorSettings.midValue && value < colorSettings.maxValue:
            return colorToRgb(
                getGradientNumbers(
                    value,
                    colorSettings.midValue,
                    colorSettings.maxValue,
                    colorSettings.colorsList[Math.floor(colorSettings.colorsList.length / 2)],
                    colorSettings.colorsList[colorSettings.colorsList.length - 1]
                )
            );
        default:
            return 'transparent';
    }
}

export function colorToRgb(gradient: Color): string {
    return `rgb(${gradient.join(',')})`;
}

export function hexToRgb(hex: string): Color {
    let r: number;
    let g: number;
    let b: number;
    [r, g, b] = hex.match(/\w\w/g).map(x => parseInt(x, 16));
    return [r, g, b];
}

export function rgbToHex(r, g, b): string {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers,no-bitwise
    return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

export function measureColumnKey(header: Partial<SelectedMeasure>): string {
    return `${header.id}${header.day ? header.day : ''}`;
}
